import type { ContextMenuItem } from "@cleo/contracts";
import type { SnoozePreset } from "@cleo/client-runtime/state/thread-settled";

/**
 * Ids for the per-thread action menu. Snooze presets are dispatched as
 * `snooze:<presetId>` so the union stays closed while the preset list
 * remains data-driven.
 */
export type ThreadActionMenuId =
  | "new-thread-on-branch"
  | "new-thread"
  | "new-sub-session"
  | "fork-session"
  | "pin"
  | "unpin"
  | "settle"
  | "unsettle"
  | "snooze"
  | `snooze:${string}`
  | "unsnooze"
  | "retire"
  | "rename"
  | "regenerate-title"
  | "mark-unread"
  | "copy-path"
  | "copy-branch"
  | "copy-thread-id"
  | "delete";

/**
 * RM-CAN-10 law 7 — ONE menu source, TWO nouns. A Local thread is a thread;
 * a World session's standing execution is spoken of as the SESSION (the
 * path of record: a thread is an execution inside a session, never the
 * thing the rail names). The noun changes the words, never the ids, so the
 * hook's dispatch stays one switch.
 */
export type ThreadActionMenuNoun = "thread" | "session";

/**
 * The seat the menu opens from (RM-CAN-10 law 7): a session row or chip in
 * the Room Rail, the chat header ("head"), or a room header. The room seat
 * has its own list (`buildRoomActionMenuItems`); the session and head seats
 * share this one, and the seat only names where the gesture came from so a
 * wearer can gate a verb by seat without a second list.
 */
export type ThreadActionMenuSeat = "session" | "head" | "room";

export interface ThreadActionMenuState {
  readonly branch: string | null;
  readonly isPinned: boolean;
  readonly isSettled: boolean;
  readonly isSnoozed: boolean;
  readonly canSnoozeNow: boolean;
  readonly isRegeneratingTitle: boolean;
  readonly supports: {
    readonly settlement: boolean;
    readonly snooze: boolean;
    readonly pinning: boolean;
    readonly titleRegeneration: boolean;
  };
  readonly snoozePresets: ReadonlyArray<SnoozePreset>;
  /** SR-CAN-03: the thread is bound to a World session AND the surface wired
   * a sibling-birth hand — unbound and Local threads never show dead
   * birth items. */
  readonly worldBound?: boolean;
  /** The noun the items speak; default "thread" (Local keeps every word). */
  readonly noun?: ThreadActionMenuNoun;
  /** Where the gesture came from; default "head". The room seat never reaches
   * this list (it has its own), so a room seat here yields the reduced list. */
  readonly seat?: ThreadActionMenuSeat;
  /** RM-CAN-10 law 8: the surface wired a retire hand — a World session's
   * one leaving verb. Absent hides the item; Snooze and Settle never
   * appear for a session (they are Local-thread parking, SR-CAN-01 law 3). */
  readonly canRetire?: boolean;
}

/**
 * Single source for the per-thread action menu: the sidebar row's right-click
 * menu, the chat header menu, and the Room Rail's rows all render exactly
 * this list, so labels, ordering, and capability gating cannot drift.
 */
export function buildThreadActionMenuItems(
  state: ThreadActionMenuState,
): ReadonlyArray<ContextMenuItem<ThreadActionMenuId>> {
  const noun = state.noun ?? "thread";
  const session = noun === "session";
  const seat = state.seat ?? "head";
  // A room seat carries no thread: only the verbs that need none survive.
  if (seat === "room") {
    return [
      { id: "copy-thread-id", label: session ? "Copy session ID" : "Copy thread ID", icon: "copy" },
    ];
  }
  return [
    ...(state.branch
      ? [
          {
            id: "new-thread-on-branch" as const,
            label: `New thread on ${state.branch}`,
          },
        ]
      : []),
    // A fresh execution stays in this Session; a sub-session is a new child,
    // and Fork creates a sibling Session. Each reaches its existing distinct
    // lifecycle door. A new root Session belongs to the room menu.
    ...(state.worldBound === true
      ? [
          { id: "new-thread" as const, label: "New thread" },
          { id: "new-sub-session" as const, label: "New sub-session" },
          { id: "fork-session" as const, label: "Fork session" },
        ]
      : []),
    ...(state.supports.pinning
      ? [
          state.isPinned
            ? { id: "unpin" as const, label: `Unpin ${noun}` }
            : { id: "pin" as const, label: `Pin ${noun}` },
        ]
      : []),
    // A World session has ONE leaving verb: retire, into the room's memory.
    ...(session && state.canRetire === true
      ? [{ id: "retire" as const, label: "Retire session" }]
      : []),
    // Both lifecycle actions stay available on pinned threads: settling
    // clears the pin ("done" beats "keep on top"), and snoozing hides the
    // card until wake with the pin intact. Neither speaks for a session.
    ...(!session && state.supports.settlement
      ? [
          state.isSettled
            ? { id: "unsettle" as const, label: "Un-settle thread" }
            : { id: "settle" as const, label: "Settle thread" },
        ]
      : []),
    ...(!session && state.supports.snooze
      ? [
          state.isSnoozed
            ? { id: "unsnooze" as const, label: "Wake thread" }
            : {
                id: "snooze" as const,
                label: "Snooze",
                disabled: !state.canSnoozeNow,
                children: state.snoozePresets.map((preset) => ({
                  id: `snooze:${preset.id}` as const,
                  label: `${preset.label} (${preset.whenLabel})`,
                })),
              },
        ]
      : []),
    { id: "rename", label: `Rename ${noun}` },
    ...(state.supports.titleRegeneration
      ? [
          {
            id: "regenerate-title" as const,
            label: state.isRegeneratingTitle ? "Regenerating…" : "Regenerate title",
            disabled: state.isRegeneratingTitle,
          },
        ]
      : []),
    { id: "mark-unread", label: "Mark unread" },
    { id: "copy-path", label: "Copy path", icon: "copy" },
    ...(state.branch ? [{ id: "copy-branch" as const, label: "Copy branch", icon: "copy" }] : []),
    { id: "copy-thread-id", label: session ? "Copy session ID" : "Copy thread ID", icon: "copy" },
    {
      id: "delete",
      label: session ? "Delete session" : "Delete",
      destructive: true,
      icon: "trash",
    },
  ];
}

/** Ids for a ROOM's menu — the Room Rail's header row (RM-CAN-10 law 7). */
export type RoomActionMenuId =
  | "room-new-session"
  | "room-rename"
  | "room-regenerate-title"
  | "room-copy-id"
  | "room-archive";

/**
 * The room header's menu, the seat beside the session menu. Rename writes the
 * room's own label through the World door; Regenerate title asks the naming
 * agent for a new word from the room's first session (2026-09-10). Archive
 * changes only the room visibility, preserving its sessions.
 */
export function buildRoomActionMenuItems(state: {
  readonly openSessions: number;
  readonly busy?: boolean;
  readonly canRegenerate?: boolean;
}): ReadonlyArray<ContextMenuItem<RoomActionMenuId>> {
  return [
    { id: "room-new-session", label: "New session in this room" },
    { id: "room-rename", label: "Rename room" },
    {
      id: "room-regenerate-title",
      label: "Regenerate title",
      disabled: state.canRegenerate === false,
    },
    { id: "room-copy-id", label: "Copy room ID", icon: "copy" },
    {
      id: "room-archive",
      label: "Archive room",
      disabled: state.busy === true,
    },
  ];
}

/** A World session's personal pin replaces the execution thread's separate pin. */
export function withSessionPinMenuItems(
  items: readonly ContextMenuItem[],
  pinned: boolean,
): ContextMenuItem[] {
  return [
    { id: "session-pin", label: pinned ? "Unpin session" : "Pin session" },
    ...items.filter((item) => item.id !== "pin" && item.id !== "unpin"),
  ];
}
